import Direction3D from './Direction3D';
import Plane3D from './Plane3D';
import Point3D from './Point3D';
import { distanceGreaterThanOrEqual } from './units';

// The set of all points on one side of a plane. It is left unspecified whether the boundary plane is included;
// method names should distinguish whether that is the case for those methods where it makes a difference.
class HalfSpace3D {
  // The direction from the half-space's exterior to the half-space's interior, perpendicular to the boundary plane.
  readonly direction: Direction3D;
  // The distance from the origin to the half-space's boundary plane, or the negative of that if the origin is interior
  // to the half-space.
  readonly offsetFromOrigin: number;

  private constructor(direction: Direction3D, offsetFromOrigin: number) {
    this.direction = direction;
    this.offsetFromOrigin = offsetFromOrigin;
  }

  static fromDirectionAndOffsetFromOrigin(direction: Direction3D, offsetFromOrigin: number) {
    return new HalfSpace3D(direction, offsetFromOrigin);
  }
  static fromDirectionAndBoundaryPoint(direction: Direction3D, point: Point3D) {
    return new HalfSpace3D(direction, direction.toPoint().dotProduct(point));
  }
  static fromPlaneAndInteriorPoint(plane: Plane3D, point: Point3D) {
    const options = plane.sides.filter((halfSpace) => halfSpace.containsPointOnInside_ZeroTolerance(point));
    return options.length === 1 ? options[0] : null;
  }

  get boundary() {
    return Plane3D.halfSpaceBoundary(this);
  }

  // Return the half-space whose interior is this half-space's exterior.
  opposite() {
    return new HalfSpace3D(this.direction.reverse(), -this.offsetFromOrigin);
  }

  containsPointOnInsideOrBoundary(point: Point3D) {
    return distanceGreaterThanOrEqual(point.dotProduct(this.direction.toPoint()), this.offsetFromOrigin);
  }

  private containsPointOnInside_ZeroTolerance(point: Point3D) {
    return point.dotProduct(this.direction.toPoint()) > this.offsetFromOrigin;
  }
}

export default HalfSpace3D;
